import React, { useState, useEffect } from "react";
import "./winnerPicker.scss";
import { useParams } from "react-router-dom";
import axios from "axios";

const WinnerPicker = () => {
  const { eventName } = useParams();
  const [contestants, setContestants] = useState("");
  const [winnerCount, setWinnerCount] = useState(1);
  const [winners, setWinners] = useState([]);
  const [isCounting, setIsCounting] = useState(false);
  const [timer, setTimer] = useState(10);
  const [showResults, setShowResults] = useState(false);
  const [loading, setLoading] = useState(false); // Used for API fetch state

  /* =========================
     Fetch Winners from API
     ========================= */
  useEffect(() => {
    const getWinners = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`/get-winners/${eventName}`);
        const emails = response.data.map((item) => item.email);

        // Convert array to string with new lines for the textarea
        setContestants(emails.join("\n"));
      } catch (error) {
        console.error("Failed to fetch participants", error);
      } finally {
        setLoading(false);
      }
    };
    if (eventName) getWinners();
  }, [eventName]);

  /* =========================
     Countdown Logic
     ========================= */
  useEffect(() => {
    let interval = null;
    if (isCounting && timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    } else if (timer === 0 && isCounting) {
      const list = contestants
        .split("\n")
        .map((item) => item.trim())
        .filter((item) => item !== "");

      const shuffled = [...list].sort(() => 0.5 - Math.random());
      const selected = shuffled.slice(0, Math.min(winnerCount, list.length));

      setWinners(selected);
      setIsCounting(false);
      setShowResults(true);
    }
    return () => clearInterval(interval);
  }, [isCounting, timer, contestants, winnerCount]);

  const handleStartDraw = () => {
    const list = contestants.split("\n").filter((t) => t.trim() !== "");
    if (list.length === 0) return alert("Please enter contestants first.");

    setTimer(10);
    setIsCounting(true);
  };

  return (
    <div className="dubai-theme-wrapper">
      {/* 1. THE MAIN PORTAL (Stays visible in background) */}
      <div className="glass-card">
        <div className="badge">Special Activation</div>

        <header className="picker-header">
          <h1>Winner Selection Portal</h1>
          <p style={{ textTransform: "capitalize" }}>
            {eventName?.replace(/-/g, " ")}
          </p>
        </header>

        {/* Keep the list visible during countdown by checking showResults only */}
        {!showResults ? (
          <div className="form-content">
            <div className="input-field">
              <label>Participant List</label>
              <textarea
                placeholder={loading ? "Loading..." : "Enter entries..."}
                value={contestants}
                disabled={isCounting || loading}
                onChange={(e) => setContestants(e.target.value)}
              />
              <span className="count-hint">
                {loading
                  ? "Syncing..."
                  : `${contestants.split("\n").filter((t) => t).length} Entries Detected`}
              </span>
            </div>

            <div className="controls">
              <div className="count-input">
                <small>Winners</small>
                <input
                  type="number"
                  value={winnerCount}
                  onChange={(e) => setWinnerCount(e.target.value)}
                  disabled={isCounting}
                />
              </div>
              <button
                className="draw-btn"
                onClick={handleStartDraw}
                disabled={isCounting || loading || !contestants}
              >
                {isCounting ? "Drawing..." : "Draw Winners"}
              </button>
            </div>
          </div>
        ) : (
          /* Winner Results Section */
          <div className="results-content animate-pop">
            <div className="winner-podium">
              <div className="icon-wrap">🏆</div>
              <h2>Official Results</h2>
              <div className="winner-scroll">
                {winners.map((name, i) => (
                  <div key={i} className="winner-row">
                    <span className="rank">#{i + 1}</span>
                    <span className="name">{name}</span>
                  </div>
                ))}
              </div>
              <button
                className="reset-link"
                onClick={() => setShowResults(false)}
              >
                ← Start New Draw
              </button>
            </div>
          </div>
        )}
      </div>

      {/* 2. THE BIG COUNTDOWN OVERLAY */}
      {/* This will pop up ON TOP of the list when isCounting is true */}
      {isCounting && (
        <div className="fullscreen-overlay">
          <div className="activation-label">SPECIAL ACTIVATION</div>
          <div id="big-number">{timer}</div>
          <div className="location-tag">{eventName}</div>
          <div className="entries-count-overlay">
            Processing {contestants.split("\n").filter((t) => t).length}{" "}
            Participants...
          </div>
        </div>
      )}
    </div>
  );
};

export default WinnerPicker;
